import React from 'react'
import './analytics-data-display.scss'

interface AnalyticsDataDisplayProps {
  content: string
  onSuggestedQuery?: (query: string) => void
}

interface ParsedSection {
  type: 'text' | 'list' | 'table' | 'summary' | 'insights' | 'suggestions'
  content: any
}

export class AnalyticsDataDisplay extends React.Component<AnalyticsDataDisplayProps> {

  private parseContent = (content: string): ParsedSection[] => {
    const sections: ParsedSection[] = []

    let currentSection: ParsedSection | null = null
    let buffer: string[] = []

    // Detect suggestions section at the end (only if formatted as bold header with bullets)
    const suggestionsMatch = content.match(/\*\*Would you like (?:me )?to:\*\*([\s\S]*?)$/i)
    let mainContent = content
    let suggestions: string[] = []

    if (suggestionsMatch) {
      mainContent = content.substring(0, suggestionsMatch.index)
      const suggestionsText = suggestionsMatch[1]
      suggestions = suggestionsText
        .split('\n')
        .map(s => s.trim())
        .filter(s => s.startsWith('-'))
        .map(s => s.replace(/^-\s*/, '').replace(/\?$/, '').trim())
        .filter(s => s.length > 0) // Filter out empty strings
    }

    // Detect markdown tables and convert to list
    const tableMatch = mainContent.match(/\|[\s\S]*?\|[\s\S]*?\|([\s\S]*?)(?=\n\n|\n[^|]|$)/m)
    if (tableMatch) {
      const tableText = tableMatch[0]
      const tableLines = tableText.split('\n').filter(line => line.trim())

      if (tableLines.length > 2) {
        const items: any[] = []

        // Start from index 2 to skip header and separator
        for (let i = 2; i < tableLines.length; i++) {
          const line = tableLines[i]
          const cells = line.split('|').map(c => c.trim()).filter(c => c)

          if (cells.length >= 2) {
            // Skip if it looks like a separator line (all dashes)
            if (cells[0].match(/^[-:]+$/)) continue

            // For 3 columns (rank, label, value), use middle and last
            // For 2 columns (label, value), use both
            const label = cells.length >= 3 ? cells[1] : cells[0]
            const value = cells.length >= 3 ? cells[2] : cells[1]

            // Don't add "clicks" suffix if value already contains it or is a percentage
            const displayValue = value.match(/clicks|%/) ? value : `${value} clicks`

            items.push({ label, value: displayValue })
          }
        }

        if (items.length > 0) {
          sections.push({ type: 'list', content: items })
          mainContent = mainContent.replace(tableText, '')
        }
      }
    }

    const contentLines = mainContent.split('\n')
    for (const rawLine of contentLines) {
      const line = rawLine.trim()

      // Skip empty lines between sections
      if (!line && !currentSection) continue

      // Skip the "Would you like to:" section - we'll handle it separately
      if (line.match(/\*\*Would you like (?:me )?to:\*\*/i)) {
        if (currentSection) {
          sections.push(currentSection)
          currentSection = null
        }
        if (buffer.length > 0) {
          sections.push({ type: 'text', content: buffer.join('\n') })
          buffer = []
        }
        break
      }

      // Detect numbered lists (1. Topic - count)
      if (/^\d+\.\s\*\*(.+?)\*\*\s*-\s*(.+)/.test(line)) {
        if (currentSection && currentSection.type !== 'list') {
          if (buffer.length > 0) {
            sections.push({ type: 'text', content: buffer.join('\n') })
            buffer = []
          }
        }

        const match = line.match(/^\d+\.\s\*\*(.+?)\*\*\s*-\s*(.+)/)
        if (match) {
          if (!currentSection || currentSection.type !== 'list') {
            currentSection = { type: 'list', content: [] }
          }
          currentSection.content.push({
            label: match[1],
            value: match[2]
          })
        }
        continue
      }

      // IMPORTANT: Check for insights BEFORE summary to avoid conflict
      // Detect insights section - matches "## Key Insights" (recommended) or legacy formats
      const insightsPattern = /^(##\s+)?(\*\*)?Key Insights:?(\*\*)?$/i
      const insightsPattern2 = /^(##\s+)?(\*\*)?Insights:?(\*\*)?$/i

      if (insightsPattern.test(line) || insightsPattern2.test(line)) {
        if (currentSection) {
          sections.push(currentSection)
        }
        if (buffer.length > 0) {
          sections.push({ type: 'text', content: buffer.join('\n') })
          buffer = []
        }
        currentSection = { type: 'insights', content: [] }
        continue
      }

      // Detect summary sections (but not insights which was checked above)
      if (line.startsWith('**') && line.endsWith(':**') && !line.toLowerCase().includes('insight')) {
        if (currentSection) {
          sections.push(currentSection)
        }
        if (buffer.length > 0) {
          sections.push({ type: 'text', content: buffer.join('\n') })
          buffer = []
        }
        currentSection = {
          type: 'summary',
          content: { title: line.replace(/\*\*/g, '').replace(':', ''), items: [] }
        }
        continue
      }

      // Detect bullet points for summary
      if (line.startsWith('- **')) {
        const match = line.match(/^-\s\*\*(.+?)\*\*\s*(.*)/)
        if (match && currentSection && currentSection.type === 'summary') {
          currentSection.content.items.push({
            label: match[1],
            value: match[2]
          })
          continue
        }
      }

      // Add insights points (with or without bullet points)
      if (currentSection && currentSection.type === 'insights') {
        if (line.startsWith('-')) {
          currentSection.content.push(line.replace(/^-\s*/, ''))
          continue
        } else if (line && !line.match(/^\*\*.*\*\*$/) && !line.match(/^would you like/i) && !line.match(/^##/)) {
          // Non-bullet insights: append to last insight if exists, or create new one
          if (currentSection.content.length > 0 && !line.match(/^[A-Z][\w\s]+:/)) {
            // Append to previous line if this doesn't start with "Label:"
            const lastIndex = currentSection.content.length - 1
            currentSection.content[lastIndex] += ' ' + line
          } else {
            currentSection.content.push(line)
          }
          continue
        } else if (!line) {
          // Empty line - skip it, don't end the section yet (might have more content after blank line)
          continue
        } else if (line.match(/^##/)) {
          // Hit another heading - end insights section
          sections.push(currentSection)
          currentSection = null
          buffer.push(line)
          continue
        }
      }

      // Add to current section or buffer
      if (currentSection) {
        if (currentSection.type === 'list' || currentSection.type === 'summary') {
          // If we hit text while in a structured section, finalize it
          if (line && !line.startsWith('-') && !line.startsWith('**')) {
            sections.push(currentSection)
            currentSection = null
            buffer.push(line)
          }
        } else if (currentSection.type === 'insights') {
          // Insights should have been handled above - if we're here, end the section
          if (line) {
            sections.push(currentSection)
            currentSection = null
            buffer.push(line)
          }
        }
      } else {
        buffer.push(line)
      }
    }

    // Push remaining content
    if (currentSection) {
      sections.push(currentSection)
    }
    if (buffer.length > 0) {
      sections.push({ type: 'text', content: buffer.join('\n') })
    }

    // Add suggestions as a separate section
    if (suggestions.length > 0) {
      sections.push({ type: 'suggestions', content: suggestions })
    }

    // CRITICAL: Reorder sections to show data (lists) first, then intro text
    // This fixes the issue where AI says "I'll query..." then shows results
    // We want: List → Text → Insights → Suggestions
    const listSections: ParsedSection[] = []
    const textSections: ParsedSection[] = []
    const otherSections: ParsedSection[] = []

    sections.forEach(section => {
      if (section.type === 'list') {
        listSections.push(section)
      } else if (section.type === 'text') {
        textSections.push(section)
      } else {
        otherSections.push(section)
      }
    })

    // Reorder: Lists first (the data), then text (explanations), then insights/suggestions
    return [...textSections,...listSections, ...otherSections]
  }

  private renderText = (text: string) => {
    // Handle markdown headings
    if (text.startsWith('###')) {
      return <h4 style={{ fontSize: '16px', fontWeight: 600, marginTop: '16px', marginBottom: '8px', color: '#2d3748' }}>{text.replace(/^###\s*/, '')}</h4>
    }
    if (text.startsWith('##')) {
      return <h3 style={{ fontSize: '18px', fontWeight: 600, marginTop: '20px', marginBottom: '12px', color: '#1a202c' }}>{text.replace(/^##\s*/, '')}</h3>
    }

    // Handle bullet points
    if (text.startsWith('- ')) {
      return (
        <div style={{ paddingLeft: '20px', marginBottom: '6px', position: 'relative' }}>
          <span style={{ position: 'absolute', left: '0', color: '#72cac2' }}>•</span>
          <span>{this.renderInlineMarkdown(text.substring(2))}</span>
        </div>
      )
    }

    return <span>{this.renderInlineMarkdown(text)}</span>
  }

  private renderInlineMarkdown = (text: string) => {
    // Handle bold text
    const parts = text.split(/(\*\*.*?\*\*)/)
    return (
      <>
        {parts.map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.slice(2, -2)}</strong>
          }
          return <span key={i}>{part}</span>
        })}
      </>
    )
  }

  private renderList = (items: any[]) => {
    return (
      <div className="analytics-data-list">
        {items.map((item, index) => (
          <div key={index} className="list-item">
            <div className="item-rank">{index + 1}</div>
            <div className="item-content">
              <div className="item-label">{item.label}</div>
              <div className="item-value">{item.value}</div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  private renderSummary = (summary: any) => {
    return (
      <div className="analytics-summary-box">
        <div className="summary-title">{summary.title}</div>
        <div className="summary-items">
          {summary.items.map((item: any, index: number) => (
            <div key={index} className="summary-item">
              <span className="summary-label">{item.label}:</span>
              <span className="summary-value">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  private renderInsights = (insights: string[]) => {
    // Don't render if empty
    if (!insights || insights.length === 0) {
      return null
    }

    return (
      <div className="analytics-insights-box">
        <div className="insights-title">
          <i className="fas fa-lightbulb"></i>
          Key Insights
        </div>
        <ul className="insights-list">
          {insights.map((insight, index) => (
            <li key={index}>{this.renderText(insight)}</li>
          ))}
        </ul>
      </div>
    )
  }

  private renderSuggestions = (suggestions: string[]) => {
    if (!this.props.onSuggestedQuery || !suggestions || suggestions.length === 0) {
      return null
    }

    return (
      <div className="analytics-suggestions-box">
        <div className="suggestions-title">
          <i className="fas fa-magic"></i>
          Next Steps
        </div>
        <div className="suggestions-grid">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              className="suggestion-btn"
              onClick={() => this.props.onSuggestedQuery && this.props.onSuggestedQuery(suggestion + '?')}
            >
              <i className="fas fa-arrow-right"></i>
              {suggestion}
            </button>
          ))}
        </div>
      </div>
    )
  }

  render() {
    const sections = this.parseContent(this.props.content)

    return (
      <div className="analytics-data-display">
        {sections.map((section, index) => {
          switch (section.type) {
            case 'list':
              return <div key={index}>{this.renderList(section.content)}</div>
            case 'summary':
              return <div key={index}>{this.renderSummary(section.content)}</div>
            case 'insights':
              return <div key={index}>{this.renderInsights(section.content)}</div>
            case 'suggestions':
              return <div key={index}>{this.renderSuggestions(section.content)}</div>
            case 'text':
            default:
              return (
                <div key={index} className="analytics-text">
                  {section.content.split('\n').map((line: string, i: number) => (
                    <p key={i}>{this.renderText(line)}</p>
                  ))}
                </div>
              )
          }
        })}
      </div>
    )
  }
}
